<template>
  <div class="animate-fade-in space-y-6">

    <!-- MODAL -->
    <div v-if="showModal" class="fixed inset-0 bg-carpatina-gri/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div class="bg-white rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] border-2 border-carpatina-verde overflow-hidden">

        <div class="bg-carpatina-verde p-5 text-white flex justify-between items-center shrink-0 shadow-md z-10">
          <h3 class="font-black uppercase tracking-widest text-lg md:text-xl flex items-center gap-2">
            <component :is="isEditing ? Pencil : PlusCircle" />
            {{ isEditing ? 'Editare Produs' : 'Material Nou' }}
          </h3>
          <button @click="closeModal" class="hover:rotate-90 transition-transform p-1 rounded-full hover:bg-white/10">
            <XCircle :size="28" />
          </button>
        </div>

        <form @submit.prevent="saveProdus" class="p-6 space-y-5 overflow-y-auto custom-scrollbar">

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="col-span-1 md:col-span-2">
              <label class="text-xs font-black uppercase text-carpatina-gri ml-1">Nume Produs</label>
              <input v-model="form.nume" required type="text"
                class="w-full p-3 mt-1 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-carpatina-portocaliu outline-none transition-colors font-bold text-gray-700">
            </div>

            <div>
              <label class="text-xs font-black uppercase text-carpatina-gri ml-1">Preț (RON)</label>
              <input v-model="form.pret" type="number" step="0.01"
                class="w-full p-3 mt-1 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-carpatina-portocaliu outline-none transition-colors font-bold text-gray-700">
            </div>

            <div>
              <label class="text-xs font-black uppercase text-carpatina-gri ml-1">Locație (Categorie)</label>
              <select v-model="form.categorie_id" required
                class="w-full p-3 mt-1 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-carpatina-portocaliu outline-none transition-colors font-bold text-gray-700 cursor-pointer">
                <option :value="null">Alege o categorie...</option>
                <option v-for="catOption in formattedCategories" :key="catOption.id" :value="catOption.id"
                  :style="catOption.level === 0 ? 'font-weight: bold; color: #00684a;' : ''">
                  {{ catOption.label }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <label class="text-xs font-black uppercase text-carpatina-gri ml-1 mb-1 block">Detalii / Specificații</label>
            <div class="bg-gray-50 rounded-xl overflow-hidden border-2 border-gray-100 focus-within:border-carpatina-portocaliu transition-colors">
              <QuillEditor
                v-model:content="form.detalii"
                contentType="html"
                theme="snow"
                toolbar="essential"
                style="min-height: 150px;"
              />
            </div>
          </div>

          <div class="flex items-center gap-3 bg-yellow-50 p-4 rounded-xl border border-yellow-200 shadow-sm">
            <input type="checkbox" id="promo" v-model="form.promovat"
              class="w-5 h-5 text-carpatina-portocaliu rounded focus:ring-carpatina-portocaliu accent-carpatina-portocaliu cursor-pointer">
            <label for="promo" class="font-black text-carpatina-gri uppercase text-xs cursor-pointer select-none flex items-center gap-2">
              <span class="text-carpatina-portocaliu">★</span> Promovează pe Home Page?
            </label>
          </div>

          <!-- Poze existente -->
          <div v-if="isEditing && form.poze && form.poze.length > 0" class="bg-white p-4 rounded-xl border-2 border-gray-100">
            <label class="text-xs font-black uppercase text-carpatina-gri mb-3 flex justify-between">
              <span>Poze Actuale</span>
              <span class="text-carpatina-verde">{{ form.poze.length }} imagini</span>
            </label>
            <div class="flex flex-wrap gap-4">
              <div v-for="(foto, index) in form.poze" :key="foto.id" class="relative group w-24 h-24 shrink-0">
                <img
                  :src="'https://carpatinaprodcom.ro/api' + foto.url"
                  :class="index === 0 ? 'border-carpatina-portocaliu ring-2 ring-carpatina-portocaliu' : 'border-gray-200'"
                  class="w-full h-full object-cover rounded-xl border shadow-sm transition-transform group-hover:scale-[1.02]"
                />
                <div v-if="index === 0"
                  class="absolute -top-2 -left-2 bg-carpatina-portocaliu text-white text-[8px] font-black px-2 py-0.5 rounded-full shadow-sm z-10 uppercase">
                  Principală
                </div>
                <!-- Buton setează principală — vizibil la TOATE pozele non-principale -->
                <button v-if="index !== 0"
                  @click.prevent="setAsPrimary(index)"
                  class="absolute bottom-1 left-1 bg-white/90 rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-all hover:bg-carpatina-portocaliu hover:text-white"
                  title="Setează ca principală">
                  <Star :size="14" />
                </button>
                <button
                  @click.prevent="deleteExistingPhoto(foto.id)"
                  class="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-md opacity-90 hover:opacity-100 hover:scale-110 transition-all z-10 group"
                  title="Șterge poza">
                  <XCircle :size="22" class="text-white group-hover:text-gray-100" fill="#ef4444" />
                </button>
              </div>
            </div>
            <p class="text-[10px] text-gray-400 italic mt-2 text-center">Hover pe o poză secundară pentru a o seta ca principală.</p>
          </div>

          <!-- Upload poze noi -->
          <div class="border-2 border-dashed border-gray-300 hover:border-carpatina-portocaliu p-8 rounded-2xl bg-gray-50 hover:bg-orange-50/30 transition-colors text-center cursor-pointer group relative">
            <input type="file" multiple @change="handleFiles" id="files" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer">
            <div class="flex flex-col items-center gap-3 pointer-events-none">
              <div class="p-3 bg-white rounded-full shadow-sm group-hover:scale-110 transition-transform">
                <ImagePlus class="text-carpatina-portocaliu" :size="32" />
              </div>
              <div class="flex flex-col">
                <span class="font-bold text-carpatina-verde uppercase text-xs tracking-wide">Click pentru a adăuga poze</span>
                <span class="text-[10px] text-gray-400 font-medium">sau trage fișierele aici</span>
              </div>
              <span v-if="selectedFiles?.length"
                class="mt-2 text-xs bg-carpatina-portocaliu text-white font-black py-1 px-3 rounded-full shadow-sm animate-pulse">
                {{ selectedFiles.length }} fișiere noi selectate
              </span>
            </div>
          </div>

          <button type="submit" :disabled="loading"
            class="w-full bg-carpatina-verde text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-carpatina-gri transition-colors shadow-lg hover:shadow-xl active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed mt-4">
            {{ loading ? 'Se salvează...' : (isEditing ? 'Salvează Modificările' : 'Adaugă în Catalog') }}
          </button>

        </form>
      </div>
    </div>

    <!-- HEADER -->
    <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-white p-6 rounded-3xl shadow-sm border-l-8 border-carpatina-verde gap-4">
      <div>
        <h2 class="text-2xl font-black text-carpatina-gri uppercase flex items-center gap-3">
          <Package class="text-carpatina-portocaliu" /> Catalog Produse
        </h2>
        <p class="text-xs text-gray-400 font-bold italic mt-1">
          Total: {{ produse.length }} produse | Afișate: {{ filteredProducts.length }}
        </p>
      </div>

      <div class="flex flex-col sm:flex-row gap-3 w-full lg:w-auto flex-wrap">

        <!-- Search -->
        <div class="relative group">
          <input v-model="searchQuery" type="text" placeholder="Caută produs..."
            class="pl-9 pr-4 py-3 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-carpatina-portocaliu focus:bg-white outline-none text-sm font-bold w-full sm:w-48 transition">
          <Search :size="18" class="absolute left-3 top-3.5 text-gray-400 group-focus-within:text-carpatina-portocaliu transition" />
        </div>

        <!-- Filtru Categorie (cu subcategorii) -->
        <select v-model="filterCategory"
          class="px-4 py-3 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-carpatina-portocaliu outline-none text-sm font-bold w-full sm:w-56 cursor-pointer">
          <option :value="null">Toate Categoriile</option>
          <option v-for="cat in formattedCategories" :key="cat.id" :value="cat.id"
            :style="cat.level === 0 ? 'font-weight: bold; color: #00684a;' : ''">
            {{ cat.label }}
          </option>
        </select>

        <!-- Filtru Promo -->
        <select v-model="filterPromo"
          class="px-4 py-3 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-carpatina-portocaliu outline-none text-sm font-bold w-full sm:w-44 cursor-pointer">
          <option :value="null">Toate Statusurile</option>
          <option :value="true">⭐ Pe Home Page</option>
          <option :value="false">Standard</option>
        </select>

        <button @click="openNewModal"
          class="bg-carpatina-verde text-white px-6 py-3 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-opacity-90 transition shadow-md whitespace-nowrap">
          <Plus :size="20" /> PRODUS NOU
        </button>
      </div>
    </div>

    <!-- TABEL -->
    <div class="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 flex flex-col">
      <div class="overflow-x-auto">
        <table class="w-full text-left whitespace-nowrap">
          <thead class="bg-carpatina-gri text-white text-xs uppercase">
            <tr>
              <th @click="setSortBy('id')" class="p-4 cursor-pointer hover:bg-carpatina-verde transition select-none">
                ID <span v-if="sortKey === 'id'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th class="p-4">Foto</th>
              <th @click="setSortBy('nume')" class="p-4 cursor-pointer hover:bg-carpatina-verde transition select-none">
                Produs <span v-if="sortKey === 'nume'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th @click="setSortBy('categorie')" class="p-4 cursor-pointer hover:bg-carpatina-verde transition select-none">
                Categorie <span v-if="sortKey === 'categorie'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th @click="setSortBy('pret')" class="p-4 cursor-pointer hover:bg-carpatina-verde transition select-none">
                Preț <span v-if="sortKey === 'pret'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th class="p-4">Stoc & Promo</th>
              <th class="p-4 text-center">Acțiuni</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="p in paginatedProducts" :key="p.id" class="hover:bg-gray-50 transition group">
              <td class="p-4 font-mono text-[10px] text-gray-400">#{{ p.id }}</td>
              <td class="p-4">
                <div class="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 shadow-sm relative">
                  <img v-if="p.poze?.length"
                    :src="'https://carpatinaprodcom.ro/api' + p.poze[0]?.url"
                    class="w-full h-full object-cover group-hover:scale-110 transition duration-500">
                  <div v-else class="w-full h-full bg-gray-100 flex items-center justify-center text-[8px] text-gray-400 font-bold uppercase">No Foto</div>
                </div>
              </td>
              <td class="p-4">
                <div class="flex flex-col">
                  <span class="font-black text-carpatina-gri uppercase text-sm">{{ p.nume }}</span>
                  <span v-if="p.promovat"
                    class="w-max mt-1 bg-yellow-100 text-yellow-600 text-[9px] px-1.5 py-0.5 rounded border border-yellow-200 font-bold flex items-center gap-1">
                    <Star :size="8" fill="currentColor" /> PROMO
                  </span>
                </div>
              </td>
              <td class="p-4">
                <span class="bg-gray-100 px-3 py-1 rounded-full text-[10px] font-black uppercase text-carpatina-verde border border-gray-200">
                  {{ p.categorie?.nume || '—' }}
                </span>
              </td>
              <td class="p-4 font-bold text-carpatina-verde text-sm">{{ p.pret ? p.pret + ' Lei' : '—' }}</td>
              <td class="p-4">
                <div class="flex flex-col gap-1.5">
                  <button @click="toggleStoc(p)"
                    :class="p.stoc ? 'text-green-600 bg-green-50' : 'text-red-500 bg-red-50'"
                    class="w-max px-2 py-1 rounded-md flex items-center gap-1 font-bold text-[10px] uppercase transition hover:opacity-80">
                    <CircleDot :size="12" /> {{ p.stoc ? 'Stoc OK' : 'Lipsă' }}
                  </button>
                  <button @click="togglePromo(p)"
                    :class="p.promovat ? 'text-yellow-600 bg-yellow-50' : 'text-gray-400 bg-gray-50'"
                    class="w-max px-2 py-1 rounded-md flex items-center gap-1 font-bold text-[10px] uppercase transition hover:opacity-80">
                    <Star :size="12" :fill="p.promovat ? 'currentColor' : 'none'" /> {{ p.promovat ? 'Pe Home' : 'Standard' }}
                  </button>
                </div>
              </td>
              <td class="p-4 text-center">
                <div class="flex justify-center gap-2">
                  <button @click="openEditModal(p)"
                    class="p-2 bg-white text-gray-400 hover:text-blue-500 hover:bg-blue-50 border border-gray-200 rounded-lg transition shadow-sm">
                    <Pencil :size="16" />
                  </button>
                  <button @click="deleteProdus(p.id)"
                    class="p-2 bg-white text-gray-400 hover:text-red-500 hover:bg-red-50 border border-gray-200 rounded-lg transition shadow-sm">
                    <Trash2 :size="16" />
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filteredProducts.length === 0 && produse.length > 0">
              <td colspan="7" class="p-12 text-center text-gray-400">
                <Search :size="32" class="mx-auto mb-2 opacity-50" />
                <p class="text-sm font-bold">Nu am găsit produse pentru filtrele selectate.</p>
                <button @click="clearFilters" class="text-carpatina-portocaliu text-xs font-black hover:underline mt-2">Resetează filtre</button>
              </td>
            </tr>
            <tr v-if="produse.length === 0">
              <td colspan="7" class="p-20 text-center">
                <div class="flex flex-col items-center opacity-30">
                  <CircleAlert :size="48" />
                  <p class="text-lg font-bold uppercase mt-4 italic">Catalogul este gol</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginare -->
      <div v-if="totalPages > 1" class="bg-gray-50 border-t border-gray-200 p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
        <span class="text-xs font-bold text-gray-400 uppercase hidden sm:block">
          Pagina {{ currentPage }} din {{ totalPages }}
        </span>
        <div class="flex gap-2 mx-auto sm:mx-0 items-center">
          <button @click="currentPage--" :disabled="currentPage === 1"
            class="p-2 rounded-lg bg-white border border-gray-200 hover:border-carpatina-portocaliu disabled:opacity-50 disabled:hover:border-gray-200 transition shadow-sm">
            <ChevronLeft :size="16" />
          </button>
          <div class="flex gap-1">
            <template v-for="(page, index) in visiblePages" :key="index">
              <span v-if="page === '...'" class="w-8 h-8 flex items-center justify-center text-gray-400 font-bold select-none text-xs">...</span>
              <button v-else @click="currentPage = page"
                :class="currentPage === page ? 'bg-carpatina-portocaliu text-white border-carpatina-portocaliu shadow-md scale-105' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'"
                class="w-8 h-8 rounded-lg border text-xs font-black transition flex items-center justify-center">
                {{ page }}
              </button>
            </template>
          </div>
          <button @click="currentPage++" :disabled="currentPage === totalPages"
            class="p-2 rounded-lg bg-white border border-gray-200 hover:border-carpatina-portocaliu disabled:opacity-50 disabled:hover:border-gray-200 transition shadow-sm">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Package, Plus, PlusCircle, Trash2, XCircle, ImagePlus,
  CircleDot, CircleAlert, Star, Search, ChevronLeft, ChevronRight, Pencil
} from 'lucide-vue-next';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

// --- STATE ---
const produse       = ref([]);
const categoriiRaw  = ref([]);
const showModal     = ref(false);
const loading       = ref(false);
const selectedFiles = ref([]);
const isEditing     = ref(false);
const currentEditId = ref(null);

// Filtre
const searchQuery    = ref('');
const filterCategory = ref(null);
const filterPromo    = ref(null);  // null = toate, true = pe home, false = standard

// Sortare
const sortKey   = ref('id');
const sortOrder = ref('desc');

// Paginare
const currentPage   = ref(1);
const itemsPerPage  = 10;

// Form
const emptyForm = () => ({
  nume:         '',
  detalii:      '',
  pret:         null,
  categorie_id: null,
  stoc:         true,
  promovat:     false,
  poze:         [],
});
const form = ref(emptyForm());

// --- HELPERS: normalizare text pentru căutare fără diacritice ---
const normalizeText = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')                        // descompune caracterele compuse
    .replace(/[\u0300-\u036f]/g, '')         // elimină diacriticele
    // fallback explicit pentru diacritice românești care pot scăpa
    .replace(/[ăâ]/g, 'a')
    .replace(/[î]/g, 'i')
    .replace(/[ș]/g, 's')
    .replace(/[ț]/g, 't')
    .replace(/[Ă Â]/gi, 'a')
    .replace(/[Î]/gi, 'i')
    .replace(/[Ș]/gi, 's')
    .replace(/[Ț]/gi, 't');
};

// --- CATEGORII ---
const formattedCategories = computed(() => {
  const options = [];
  const buildTree = (parentId = null, level = 0) => {
    const children = categoriiRaw.value
      .filter(c => c.parent_id === parentId)
      .sort((a, b) => a.nume.localeCompare(b.nume));
    children.forEach(child => {
      const prefix = level > 0 ? '\u00A0\u00A0\u00A0'.repeat(level) + '↳ ' : '';
      options.push({ id: child.id, label: prefix + child.nume, level });
      buildTree(child.id, level + 1);
    });
  };
  buildTree(null, 0);
  return options;
});

// Returnează ID-urile categoriei + toate subcategoriile (recursiv)
const getAllDescendantIds = (rootId) => {
  let ids = [rootId];
  categoriiRaw.value
    .filter(c => c.parent_id === rootId)
    .forEach(child => { ids = ids.concat(getAllDescendantIds(child.id)); });
  return ids;
};

// --- PRODUSE FILTRATE & SORTATE ---
const filteredProducts = computed(() => {
  const query = normalizeText(searchQuery.value);

  let list = produse.value.filter(p => {
    // Căutare cu normalizare diacritice
    const matchText = !query ||
      normalizeText(p.nume).includes(query);

    // Filtrare categorie + subcategorii
    const matchCat = !filterCategory.value ||
      getAllDescendantIds(filterCategory.value).includes(p.categorie_id);

    // Filtrare promo
    const matchPromo = filterPromo.value === null ||
      Boolean(p.promovat) === filterPromo.value;

    return matchText && matchCat && matchPromo;
  });

  // Sortare
  return list.sort((a, b) => {
    const mod = sortOrder.value === 'asc' ? 1 : -1;
    if (sortKey.value === 'id') {
      return (a.id - b.id) * mod;
    }
    if (sortKey.value === 'pret') {
      return ((a.pret || 0) - (b.pret || 0)) * mod;
    }
    if (sortKey.value === 'categorie') {
      const cA = a.categorie?.nume?.toLowerCase() || '';
      const cB = b.categorie?.nume?.toLowerCase() || '';
      return cA.localeCompare(cB) * mod;
    }
    // Sortare generică (nume etc.)
    const vA = (a[sortKey.value] || '').toString().toLowerCase();
    const vB = (b[sortKey.value] || '').toString().toLowerCase();
    return vA.localeCompare(vB) * mod;
  });
});

const setSortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value  = key;
    sortOrder.value = 'asc';
  }
};

// --- PAGINARE ---
const totalPages = computed(() => Math.ceil(filteredProducts.value.length / itemsPerPage));

const paginatedProducts = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return filteredProducts.value.slice(start, start + itemsPerPage);
});

const visiblePages = computed(() => {
  const total   = totalPages.value;
  const current = currentPage.value;
  const delta   = 1;
  const range   = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const result = [];
  let prev;
  for (const i of range) {
    if (prev) {
      if (i - prev === 2) result.push(prev + 1);
      else if (i - prev > 2) result.push('...');
    }
    result.push(i);
    prev = i;
  }
  return result;
});

watch([searchQuery, filterCategory, filterPromo], () => {
  currentPage.value = 1;
});

const clearFilters = () => {
  searchQuery.value    = '';
  filterCategory.value = null;
  filterPromo.value    = null;
};

// --- FETCH ---
const fetchData = async () => {
  try {
    const [resP, resC] = await Promise.all([
      axios.get('/produse'),
      axios.get('/categorii'),
    ]);
    produse.value      = Array.isArray(resP.data) ? resP.data : [];
    categoriiRaw.value = Array.isArray(resC.data) ? resC.data : [];
  } catch (err) {
    console.error('Eroare la preluarea datelor:', err);
    produse.value = [];
  }
};

// --- MODAL ---
const openNewModal = () => {
  isEditing.value     = false;
  currentEditId.value = null;
  form.value          = emptyForm();
  selectedFiles.value = [];
  showModal.value     = true;
};

const openEditModal = (produs) => {
  isEditing.value     = true;
  currentEditId.value = produs.id;
  form.value = {
    nume:         produs.nume,
    detalii:      produs.detalii,
    pret:         produs.pret,
    categorie_id: produs.categorie_id,
    stoc:         produs.stoc,
    promovat:     produs.promovat,
    poze:         produs.poze ? [...produs.poze] : [],
  };
  selectedFiles.value = [];
  showModal.value     = true;
};

const closeModal = () => {
  showModal.value = false;
  setTimeout(() => {
    isEditing.value     = false;
    currentEditId.value = null;
    form.value          = emptyForm();
    selectedFiles.value = [];
  }, 300);
};

// --- SAVE ---
const handleFiles = (e) => { selectedFiles.value = Array.from(e.target.files); };

const saveProdus = async () => {
  if (!form.value.categorie_id) {
    Swal.fire('Atenție', 'Te rog selectează o categorie!', 'warning');
    return;
  }
  loading.value = true;
  try {
    const fd = new FormData();
    fd.append('nume',         form.value.nume);
    fd.append('detalii',      form.value.detalii);
    fd.append('categorie_id', form.value.categorie_id);
    fd.append('stoc',         form.value.stoc);
    fd.append('promovat',     form.value.promovat);
    if (form.value.pret) fd.append('pret', form.value.pret);

    if (isEditing.value) {
      await axios.patch(`/admin/produs/${currentEditId.value}`, fd);
      if (selectedFiles.value.length > 0) {
        const fdPoze = new FormData();
        selectedFiles.value.forEach(file => fdPoze.append('files', file));
        await axios.post(`/admin/produs/${currentEditId.value}/poze`, fdPoze);
      }
      Swal.fire({ title: 'Actualizat!', icon: 'success', timer: 1500, showConfirmButton: false });
    } else {
      selectedFiles.value.forEach(file => fd.append('files', file));
      await axios.post('/admin/produs', fd);
      Swal.fire({ title: 'Adăugat!', icon: 'success', timer: 1500, showConfirmButton: false });
    }

    closeModal();
    fetchData();
  } catch (err) {
    console.error(err);
    Swal.fire('Eroare', 'Nu s-a putut salva produsul.', 'error');
  } finally {
    loading.value = false;
  }
};

// --- POZE ---
// Setează poza de la `index` ca principală (index 0)
const setAsPrimary = async (index) => {
  // 1. Mutăm local poza selectată pe poziția 0
  const poze = [...form.value.poze];
  const [selected] = poze.splice(index, 1);
  poze.unshift(selected);
  form.value.poze = poze;

  try {
    // 2. Trimitem noua ordine la backend
    const photo_ids = form.value.poze.map(p => p.id);
    await axios.patch(`/admin/produs/${currentEditId.value}/reorder-photos`, { photo_ids });
    Swal.fire({ icon: 'success', title: 'Imagine principală actualizată!', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
  } catch (err) {
    console.error('Eroare la reordonare:', err);
    // Modificarea vizuală s-a aplicat deja local; backend-ul poate fi implementat ulterior
  }
};

const deleteExistingPhoto = async (pozaId) => {
  const result = await Swal.fire({
    title: 'Ștergi poza?', icon: 'warning',
    showCancelButton: true, confirmButtonColor: '#d33',
    confirmButtonText: 'Da, șterge', cancelButtonText: 'Nu',
  });
  if (result.isConfirmed) {
    try {
      await axios.delete(`/admin/poza/${pozaId}`);
      form.value.poze = form.value.poze.filter(p => p.id !== pozaId);
      Swal.fire({ icon: 'success', title: 'Poza a fost ștearsă!', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
    } catch (err) {
      Swal.fire('Eroare', 'Nu s-a putut șterge poza.', 'error');
    }
  }
};

// --- TOGGLE STOC / PROMO ---
const toggleStoc = async (p) => {
  try {
    const fd = new FormData();
    fd.append('stoc', !p.stoc);
    await axios.patch(`/admin/produs/${p.id}`, fd);
    p.stoc = !p.stoc;
  } catch (err) {
    Swal.fire('Eroare', 'Nu s-a putut schimba stocul.', 'error');
  }
};

const togglePromo = async (p) => {
  try {
    const fd = new FormData();
    fd.append('promovat', !p.promovat);
    await axios.patch(`/admin/produs/${p.id}`, fd);
    p.promovat = !p.promovat;
  } catch (err) {
    Swal.fire('Eroare', 'Nu s-a putut schimba statusul promo.', 'error');
  }
};

// --- DELETE ---
const deleteProdus = async (id) => {
  const result = await Swal.fire({
    title: 'Ștergi produsul?', icon: 'warning',
    showCancelButton: true, confirmButtonColor: '#d33',
    confirmButtonText: 'DA', cancelButtonText: 'NU',
  });
  if (result.isConfirmed) {
    try {
      await axios.delete(`/admin/produs/${id}`);
      produse.value = produse.value.filter(p => p.id !== id);
      Swal.fire('Șters!', '', 'success');
    } catch (err) {
      Swal.fire('Eroare', '', 'error');
    }
  }
};

onMounted(fetchData);
</script>

<style scoped>
.animate-fade-in { animation: fadeIn 0.4s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

select option { padding: 10px; }

:deep(.ql-toolbar) {
  border: none !important;
  border-bottom: 2px solid #f3f4f6 !important;
  background-color: #fff;
}
:deep(.ql-container) {
  border: none !important;
  font-family: inherit;
  font-size: 0.95rem;
}
:deep(.ql-editor) {
  min-height: 150px;
  max-height: 200px;
  overflow-y: auto;
}
:deep(.ql-editor)::-webkit-scrollbar { width: 8px; }
:deep(.ql-editor)::-webkit-scrollbar-thumb { background-color: #d1d5db; border-radius: 4px; }
</style>